import { useState } from "react";
import { Alert } from "../alert/Alert";
import { Button } from "@sellar-io/components";
import {
  SingleFieldDialog,
  SingleFieldDialogImage,
  SingleFieldDialogHeader,
  SingleFieldDialogCTA,
} from "@sellar-io/components";
import { apiV2AssertError } from "~/hooks/api/v2/apiV2Request";
import { pluralize } from "@sellar-io/common";

interface PostcodeMoveDialogProps {
  active: boolean;
  postcodeCount: number;
  onDismiss: () => void;
  onMove: () => Promise<void>;
  isMoving: boolean;
}

export const PostcodeMoveDialog = ({
  active,
  postcodeCount,
  onDismiss,
  onMove,
  isMoving,
}: PostcodeMoveDialogProps) => {
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMove = async () => {
    if (!checked) return;

    setError(null);
    try {
      await onMove();
    } catch (e) {
      const assertedError = apiV2AssertError(e);
      setError(
        assertedError?.response?.data.message ?? "Failed to move postcodes",
      );
    }
  };

  return (
    <SingleFieldDialog active={active} onDismiss={onDismiss} size="large">
      <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/app-assets/delivery-zone-move-postcode.png" />
      <SingleFieldDialogHeader
        title={`Move ${postcodeCount} ${pluralize("postcode", postcodeCount)} from their current delivery zone(s)?`}
        description="Postcodes can only appear in one delivery zone so that we know the correct  delivery methods to show the customers in that postcode. If you want to include this postcode in this delivery zone, you can move it."
        icon="INFORMATION"
        iconClassName="bg-amber-50 text-amber-600"
      />

      {error && <Alert type="error" message={error} />}

      <SingleFieldDialogCTA className="items-center">
        <label className="mr-auto ml-4 flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          I confirm I want to move these postcodes
        </label>

        <Button color="transparent" onClick={onDismiss} disabled={isMoving}>
          Cancel
        </Button>
        <Button
          loading={isMoving}
          disabled={!checked || isMoving}
          onClick={handleMove}
        >
          Move
        </Button>
      </SingleFieldDialogCTA>
    </SingleFieldDialog>
  );
};
